import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import SyncRounded from "@mui/icons-material/SyncRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import PartnerDirectoryEdgeSync from "../../components/PartnerDirectoryEdgeSync";
import { PdConflictReadNotice } from "./PdConflictResolver";
import { cicdMonitorUrl } from "../../components/CicdExecution";
import { v } from "../../utils/helpers";

// Zustandsfarbe als Text (keine Pills), nur CSS-Variablen.
export const STATE_COLOR = { reached: "success", running: "accent", failed: "danger", "deployment-failed": "danger", partial: "warning", absent: "text-3", "pending-proof": "text-2" };
const STATES = new Set(Object.keys(STATE_COLOR));

// Edge-Zustand der Teilerfolgszelle; ohne edgeSync aus execution.edge abgeleitet (Vertrag 6.2).
const edgeStateOf = (edgeSync, execution) => edgeSync?.state
  || (execution?.edge === "pending" ? "running" : execution?.edge === "failed" ? "failed" : null);

/**
 * Zustand einer Stufenzelle (Vertrag 6.3, 6.5, 16.2).
 * `compact`: Nur Zustandstext und Fortschritt, keine Aktionen (Kartenmodus).
 *            Auf der Karte zeigt die Fusszeile den Monitor-Link und die Aktionen stehen im Details-Dialog.
 */
export default function StageCellState({ state, execution, blockedReason, edgePending, edgeSync, packageId, spaceId, onRetry, instanceName, returnFocusRef, pending, compact, pdConflict }) {
  const { t } = useTranslation("freigabe");
  const [syncOpen, setSyncOpen] = useState(false);
  const titleId = useId();
  const syncTrigger = useRef(null);
  const closeSync = () => {
    setSyncOpen(false);
    requestAnimationFrame(() => (returnFocusRef?.current || syncTrigger.current)?.focus?.());
  };
  const known = STATES.has(state);
  const partial = state === "partial";
  const edgeState = partial ? edgeStateOf(edgeSync, execution) : null;
  const monitorHref = !compact && execution && ["running", "failed", "partial", "deployment-failed"].includes(state)
    ? cicdMonitorUrl({ spaceId, runId: execution.runId, operationId: execution.requestId, packageId }) : null;
  const canRetry = !compact && ["failed", "deployment-failed"].includes(state) && typeof onRetry === "function" && !!execution?.requestId && !pending;
  const canSync = !compact && partial && edgeSync?.state === "failed" && edgeSync.canSync === true && (edgeSync.partnerIds || []).length > 0 && !pending;

  return <Stack spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
    {/* Im Kartenmodus (compact) entfaellt „Transport fehlgeschlagen" als Text; der Fehlerzustand wird als roter Punkt an der Kartenecke gezeigt. */}
    {known && state !== "reached" && !(compact && state === "failed") && <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: v(STATE_COLOR[state]) }}>
      {t(`stageBranches.state.${state}`)}
    </Typography>}
    {state === "running" && <LinearProgress aria-label={t("stageBranches.cell.progress")} sx={{ width: "100%", minWidth: 120 }}/>}
    {/* Kompakt: keine Edge-Hinweise, keine Aktionen */}
    {!compact && partial && edgePending && !edgeSync?.state && <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>
      {t("stageBranches.hints.EDGE_PENDING")}
    </Typography>}
    {!compact && edgeState && <Typography component="span" variant="body2" role="status"
      sx={{ fontWeight: 600, color: v(edgeState === "failed" ? "danger" : "warning") }}>
      {t(`stageBranches.edge.${edgeState}`)}
    </Typography>}
    {(canRetry || canSync || monitorHref) && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {canRetry && <MonitorButton variant="outlined" size="small" startIcon={<ReplayRounded/>}
        onClick={() => onRetry(execution.requestId, [packageId])}>{t("stageBranches.cell.retry")}</MonitorButton>}
      {canSync && <MonitorButton ref={syncTrigger} variant="outlined" size="small" startIcon={<SyncRounded/>} aria-haspopup="dialog"
        onClick={() => setSyncOpen(true)}>{t("stageBranches.edge.sync")}</MonitorButton>}
      {monitorHref && <MonitorButton component="a" variant="text" size="small" href={monitorHref} target="_blank" rel="noopener noreferrer"
        endIcon={<OpenInNewRounded/>}>{t("stageBranches.cell.monitor")}</MonitorButton>}
    </Stack>}
    {!compact && blockedReason && <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>
      {t(`promotion.blocked.${blockedReason}`)}
    </Typography>}
    {syncOpen && canSync && <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="sm" aria-labelledby={titleId} onClose={closeSync}>
      <DialogTitle id={titleId}>{t("stageBranches.edge.sync")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: v("text-2"), mb: 2 }}>{t("stageBranches.edge.dialogIntro")}</Typography>
        <PdConflictReadNotice error={pdConflict?.readError} onRetry={pdConflict?.refresh}/>
        {edgeSync.partnerIds.map(pid =>
          <PartnerDirectoryEdgeSync key={`${edgeSync.instanceId}:${pid}`} compact pidLabel={pid}
            instanceId={edgeSync.instanceId} instanceName={instanceName || edgeSync.instanceId} pid={pid}/>)}
      </DialogContent>
      <DialogActions>
        <MonitorButton autoFocus onClick={closeSync}>{t("stageBranches.edge.close")}</MonitorButton>
      </DialogActions>
    </MonitorDialog>}
  </Stack>;
}

/* --- Kartenfuss ---------------------------------------------------------- */

/** Icon-Aktionen in einer gleichmaessig verteilten Fusszeile der Karte: Wiederholen (nur wenn moeglich), Monitor, Details, Entfernen. */
export function CardFooter({ onRetry, retryLabel, retryDisabled, monitorHref, onDetails, onRemove, removeDisabled, removeDisabledReason, t }) {
  const items = [];
  if (onRetry) items.push({ key: "retry", label: retryLabel || t("stageBranches.cell.retry"), icon: <ReplayRounded fontSize="small"/>,
    props: { onClick: retryDisabled ? undefined : onRetry, disabled: !!retryDisabled } });
  if (monitorHref) items.push({ key: "monitor", label: t("stageBranches.cell.monitor"), icon: <OpenInNewRounded fontSize="small"/>,
    props: { component: "a", href: monitorHref, target: "_blank", rel: "noopener noreferrer" } });
  if (onDetails) items.push({ key: "details", label: t("stageBranches.details.toggle"), icon: <InfoOutlined fontSize="small"/>,
    props: { onClick: onDetails } });
  if (onRemove) items.push({ key: "remove", label: removeDisabledReason || t("stageBranches.removal.action"), icon: <RemoveCircleOutlineRounded fontSize="small"/>,
    props: { onClick: removeDisabled ? undefined : onRemove, disabled: !!removeDisabled } });
  if (!items.length) return null;
  return <Box className="sb-card-footer" sx={{ display: "flex", borderTop: "1px solid var(--iio-border)", mt: "6px", pt: "4px" }}>
    {items.map(item => <Tooltip key={item.key} title={item.label} arrow>
      <span>{/* span wrapper so Tooltip works on disabled buttons */}
      <IconButton size="small" aria-label={item.label} {...item.props}
        sx={{ flex: "1 1 0", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-2)", "&:hover": { color: "var(--iio-text-1)", background: "var(--iio-bg-2)" },
          "&.Mui-disabled": { color: "var(--iio-text-3)" } }}>
        {item.icon}
      </IconButton>
      </span>
    </Tooltip>)}
  </Box>;
}

/* --- Fehlerpunkt --------------------------------------------------------- */

/** Roter halbrunder Punkt in der oberen rechten Ecke der Karte (folgt dem Kartenradius). */
export function FailedDot({ t, label = t("stageBranches.state.failed") }) {
  return <Tooltip title={label} arrow>
    <Box aria-label={label} role="img"
      sx={{ position: "absolute", top: 0, right: 0, width: 14, height: 14, borderRadius: "0 var(--iio-r-lg) 0 var(--iio-r-lg)",
        background: "var(--iio-critical)", pointerEvents: "auto" }}/>
  </Tooltip>;
}

/** Orangefarbener Punkt fuer Aufmerksamkeit (partial, pending-proof, unknown). */
export function AttentionDot({ label }) {
  return <Tooltip title={label} arrow>
    <Box aria-label={label} role="img"
      sx={{ position: "absolute", top: 0, right: 0, width: 14, height: 14, borderRadius: "0 var(--iio-r-lg) 0 var(--iio-r-lg)",
        background: "var(--iio-warning)", pointerEvents: "auto" }}/>
  </Tooltip>;
}

/* --- Meta-Tabelle -------------------------------------------------------- */

const META_CELL = { px: 0, py: "2px", border: 0, typography: "body2", lineHeight: 1.4, fontFamily: "inherit", verticalAlign: "top" };

/** Kompakte zweispaltige Metatabelle in der Karte. */
export function CardMetaTable({ rows }) {
  const visible = rows.filter(r => r);
  if (!visible.length) return null;
  return <Table size="small" aria-hidden="true" sx={{ mt: "4px", tableLayout: "fixed" }}>
    <TableBody>
      {visible.map((row, i) => <TableRow key={row.key || i} sx={{ "&:last-of-type td": { pb: 0 } }}>
        <TableCell sx={{ ...META_CELL, width: "40%", color: "var(--iio-text-2)", pr: 1 }}>{row.label}</TableCell>
        <TableCell sx={{ ...META_CELL, color: "var(--iio-text-1)", overflowWrap: "anywhere" }}>{row.value}</TableCell>
      </TableRow>)}
    </TableBody>
  </Table>;
}
